'use es6';

import CssVariablesTheme from 'foundations-theming/css-vars-theme';
import I18n from 'I18n';
import { Map as ImmutableMap, Set as ImmutableSet } from 'immutable';
import { getFiscalYearBackwardsOffset } from 'reporting-data/lib/fiscalYearUtils';
import { ALLOWED_STACK_VALUES } from 'reporting-visualizations/constants/stacking';
import { FormatTypes } from 'reporting-visualizations/formatters/propertyFormatter';
import { AggregateTypes } from 'reporting-visualizations/transforms/aggregate';
import { AccumulateTransform, TransformTypes } from 'reporting-visualizations/transforms/transformRecords';
import { toVisualizationOrder } from '../../modify/sorts/report-sort-utils';
import { GOAL_ID } from '../../resolve/goal';
import { DateTruncationTypes } from '../../schema/column-records';
import { getDateResolutionForColumn, hasDateTrunc, shouldApplyFiscalYearOffset } from '../../utils/column-utils';
import { getEncodingFormatParameters, getEncodingFormatType } from '../../utils/visual-utils';
import { computeAssignments, computePropertyAssignments } from '../color/assign';
import { collectDimensionBuckets, collectMeasureBuckets } from '../color/bucket';
import { getColorPalette } from '../color/utils';
import { getDeprecatedDefaultPalettes } from '../color/palettes';
const calculateTodayLineValue = (column, fiscalYearMonthName) => {
  const dateResolution = getDateResolutionForColumn(column);
  const now = I18n.moment.portalTz();
  if (!shouldApplyFiscalYearOffset(column)) {
    return now.startOf(dateResolution).format('YYYY-MM-DD');
  }
  return now.startOf('month').add(getFiscalYearBackwardsOffset(fiscalYearMonthName), 'months').startOf(dateResolution).format('YYYY-MM-DD');
};
export function getTodayLine(dateColumn, fiscalYearMonthName) {
  if (!hasDateTrunc(dateColumn)) {
    return undefined;
  }
  const dateResolution = getDateResolutionForColumn(dateColumn);
  if (!dateResolution) {
    return undefined;
  }
  const dateResolutionToLabel = {
    [DateTruncationTypes.DAY]: 'reporting-snowflake.dateline.day',
    [DateTruncationTypes.WEEK]: 'reporting-snowflake.dateline.week',
    [DateTruncationTypes.MONTH]: 'reporting-snowflake.dateline.month',
    [DateTruncationTypes.QUARTER]: 'reporting-snowflake.dateline.quarter',
    [DateTruncationTypes.YEAR]: 'reporting-snowflake.dateline.year'
  };
  const targetCategoryValue = calculateTodayLineValue(dateColumn, fiscalYearMonthName);
  return {
    id: 'todayLine',
    dashStyle: 'Dash',
    color: CssVariablesTheme['color']['do-not-use-eerie'],
    width: 1,
    label: {
      text: I18n.text(dateResolutionToLabel[dateResolution]),
      rotation: 0,
      style: {
        color: CssVariablesTheme['color']['do-not-use-eerie']
      }
    },
    targetCategoryValue
  };
}
export function getGoalTransform(groupField, colorEncoding, shouldAccumulate, accumulateAlong) {
  const transforms = [];
  if (colorEncoding && colorEncoding.column) {
    transforms.push({
      type: TransformTypes.AGGREGATE,
      groupBy: [groupField],
      aggregations: [{
        op: AggregateTypes.attribute,
        field: GOAL_ID,
        as: GOAL_ID
      }]
    });
  }
  if (shouldAccumulate && accumulateAlong) {
    transforms.push(AccumulateTransform({
      groupBy: [],
      field: GOAL_ID,
      along: accumulateAlong
    }));
  }
  return transforms;
}
export function getGoalLayer(showMarkers, x, dataset, colorEncoding, shouldAccumulate, accumulateAlong) {
  const xField = x && x.column.alias;
  return {
    dataset: 'primary',
    mark: {
      type: 'line',
      zIndex: 10,
      marker: {
        enabled: showMarkers,
        zIndex: 10
      }
    },
    x: x ? {
      type: 'category',
      field: xField,
      blend: 'bottom',
      order: toVisualizationOrder(x.column, dataset)
    } : undefined,
    y: {
      type: 'linear',
      field: GOAL_ID,
      blend: 'left',
      fill: true
    },
    transform: getGoalTransform(xField, colorEncoding, shouldAccumulate, accumulateAlong),
    color: {
      default: CssVariablesTheme['color']['do-not-use-battleship']
    },
    excludeFromLegacyTooltipTotals: true
  };
}

/**
 * @param {Column?} colorColumn
 * @param {List<Column>?} measureColumns
 * @param {Dataset} dataset
 * @param {ColorOption} colorOption
 * @returns high charts color encoding
 */
export const toXYColor = (colorColumn, measureColumns, dataset, colorOption, colorEncoding, brandKitPalettes, colorProperty = undefined, palettes) => {
  const order = colorColumn && toVisualizationOrder(colorColumn, dataset);
  const buckets = colorColumn === undefined && measureColumns ? collectMeasureBuckets(measureColumns, dataset) : collectDimensionBuckets(colorColumn, dataset);
  const {
    scheme
  } = getColorPalette(colorOption, brandKitPalettes, (palettes === null || palettes === void 0 ? void 0 : palettes.DEFAULT_PALETTES) || getDeprecatedDefaultPalettes('shared-mapper-fallback'), palettes === null || palettes === void 0 ? void 0 : palettes.DefaultCategorical);
  const assignments = computeAssignments(buckets, scheme, colorOption.assignments);
  if (assignments.isEmpty()) {
    return undefined;
  }
  const patternAssignments = computePropertyAssignments(buckets, scheme.patterns);
  if (colorColumn === undefined) {
    const alias = measureColumns && measureColumns.size === 1 ? measureColumns.get(0).get('alias') : colorEncoding && colorEncoding.column && colorEncoding.column.alias;
    return {
      default: alias ? assignments.get(alias) : undefined,
      defined: assignments.toJS(),
      definedPatterns: patternAssignments && patternAssignments.toJS(),
      defaultPattern: alias && patternAssignments ? patternAssignments.get(alias) : undefined,
      formattingType: getEncodingFormatType(colorEncoding),
      formattingObject: getEncodingFormatParameters(colorEncoding, colorProperty)
    };
  }
  const colorId = colorColumn.alias;
  return {
    field: colorId,
    defined: assignments.toJS(),
    definedPatterns: patternAssignments && patternAssignments.toJS(),
    order,
    formattingType: getEncodingFormatType(colorEncoding),
    formattingObject: getEncodingFormatParameters(colorEncoding, colorProperty)
  };
};
export const getMeasureAxisLabel = (labelSetting, measureEncodings, dataset) => {
  const label = labelSetting != null ? labelSetting : measureEncodings && measureEncodings.size > 0 ? measureEncodings.get(0).column.name || dataset && dataset.properties && dataset.properties.getIn([measureEncodings.get(0).column.alias, 'label']) : undefined;
  return label;
};
export const doMeasuresHaveSameFormattingType = (measureEncodings, dataset) => {
  if (measureEncodings.length === 0) {
    return true;
  }
  const formattingTypes = measureEncodings.reduce((nextTypeSet, measureEncoding, idx) => {
    const columnAlias = measureEncoding.column.alias;
    const property = dataset.properties.get(columnAlias);
    const format = measureEncoding.options.format;
    if (format && format.type !== FormatTypes.AUTO) {
      if (format.type === FormatTypes.CURRENCY) {
        return nextTypeSet.add(format.type + format.currencyCode);
      }
      return nextTypeSet.add(format.type);
    }
    if (property) {
      if (property.get('type') === FormatTypes.CURRENCY) {
        return nextTypeSet.add(property.get('type') + property.get('companyCurrencyCode'));
      }
      return nextTypeSet.add(property.get('type'));
    }
    return nextTypeSet.add(idx);
  }, ImmutableSet());
  return formattingTypes.size === 1;
};
export const getMultiMeasureFormatting = measureEncodings => {
  if (measureEncodings.length === 0) {
    return ImmutableMap();
  }
  const formattingObjects = measureEncodings.reduce((nextFormattingObjects, measureEncoding) => {
    const columnAlias = measureEncoding.column.alias;
    const format = measureEncoding.options.format;
    return nextFormattingObjects.set(columnAlias, format);
  }, ImmutableMap());
  return formattingObjects;
};
export const processReportStacking = (stacking, stackingType) => {
  if (!stacking) {
    // the builder treats false and undefined/null as separate entities so cannot just return false/null here
    return stacking;
  }
  const isValidStackingType = ALLOWED_STACK_VALUES.includes(stackingType);
  return isValidStackingType ? stackingType : true;
};